/* eslint complexity: ["error", 60] */

import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Container, Fade, Tabs, Tab, Button, Badge, Spinner} from 'react-bootstrap';
import CommonMetaTag from './common-meta-tag';
import Dialog from 'react-bootstrap-dialog';
import binIcon from '../images/bin_icon.svg';
import Configuration from '../config';
import * as api from '../api';
import {push} from 'connected-react-router';
import {addVideoToPlayerArea, deleteRtvVideo, addRtvVideoPlaying, getRtvVideos, permanentVideoSelected, postUsageData, showVideoHighlightModal} from '../actions/player';
import PlayerHighLight from './player-highlight';
import replayerlogoBianco from '../images/replayer_logo_bianco_40x32px.png';
import noLogo from '../images/club_icon_grey.svg';
import specialEvent from '../images/special_event.svg';
import { rtvType } from '../constants';
import {useTranslation} from 'react-i18next';
import {getVideoFieldSport, isPickleballVideo} from '../util/video-sports';
import {openVideoPlayer, preconnectVideoPlayer} from '../utils/video-player-preload';
import {shouldPollRtvVideos} from '../util/special-event-access';

const PlayerReplayerTv = ({
  token,
  selectedPartner,
  rtvVideos,
  rtvVideosSoon,
  addVideoToPlayerArea,
  deleteRtvVideo,
  profile,
  rtvVideosPlaying,
  addRtvVideoPlaying,
  updateVideos,
  selectedRtvChannel,
  selectedSpecialEvent,
  sendUsageData,
  selectedPermanentVideo,
  createSliceHighlight,
  createSliceAnalysis,
}) => {
  const {t} = useTranslation();
  const [videoNotice, setVideoNotice] = useState(null);
  const [videoReadyNotificationLoadingId, setVideoReadyNotificationLoadingId] = useState(null);
  useEffect(() => {
    const processing = shouldPollRtvVideos(rtvVideos);
    const scheduled = Array.isArray(rtvVideosSoon) && rtvVideosSoon.length > 0;
    if (!token || !selectedPartner || !selectedRtvChannel || (!processing && !scheduled)) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        updateVideos(
          {partnerUid: selectedPartner.uid, id: selectedRtvChannel.id},
          selectedSpecialEvent ? selectedSpecialEvent.id : null
        );
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [rtvVideos, rtvVideosSoon, selectedPartner, selectedRtvChannel, selectedSpecialEvent, token, updateVideos]);

  if (token === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  if (!selectedPartner || !selectedRtvChannel) {
    return null;
  }

  const rtvPlayerUrl = v => `${Configuration.krpanoPlayerUrl}?slotId=${v.id}&token=${v.token}`;
  const openPlayerUrl = url => {
    openVideoPlayer(url);
  };

  const playRtvVideo = v => () => {
    openPlayerUrl(rtvPlayerUrl(v));
    sendUsageData(selectedPartner.uid, selectedRtvChannel.id, v.id, !v.videoComplete);
  };

  const styleBackgroundImage = () => {
    if (selectedRtvChannel.imagesNames && selectedRtvChannel.imagesNames.length > 0) {
      return {backgroundImage: `url(${Configuration.apiEndPoint}/partners/rtv_channels/${selectedRtvChannel.id}/images?load=${selectedRtvChannel.imagesNames[0]})`};
    }

    return null;
  };

  let dialog = null;
  const confirmDeleteRtvVideo = slotId => () => {
    dialog.show({
      title: t('CONFIRM_DELETION_FROM_RTV'),
      body: t('DEACTIVATING_OPTION_VIDEO_FROM_RTV'),
      actions: [
        Dialog.Action(
          t('YES'),
          deleteRtvVideo(slotId),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('CANCEL'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const showVideoInProgressNotice = video => () => {
    setVideoNotice({
      contentSection: 'processing',
      title: t('UPLOAD_IN_PROGRESS'),
      body: t('RTV_VIDEO_PROCESSING_HELP'),
      video,
    });
  };

  const showVideoScheduledNotice = video => () => {
    setVideoNotice({
      contentSection: 'scheduled',
      title: t('VIDEO_SCHEDULED_TITLE'),
      body: t('VIDEO_SCHEDULED_INFO'),
      video,
    });
  };

  const subscribeVideoReadyNotification = async video => {
    setVideoReadyNotificationLoadingId(video.id);
    const result = await api.postPublicVideoReadyNotification(video.id, token);
    setVideoReadyNotificationLoadingId(null);

    if (result.status === 200 || result.status === 201) {
      setVideoNotice({
        contentSection: result.json && result.json.videoComplete ? 'ready' : 'saved',
        title: result.json && result.json.videoComplete ? t('RTV_VIDEO_ALREADY_READY') : t('RTV_REQUEST_SAVED'),
        body: result.json && result.json.videoComplete
          ? t('RTV_VIDEO_ALREADY_READY_HELP')
          : t('RTV_READY_EMAIL_HELP'),
        video,
      });
      return;
    }

    setVideoNotice({
      contentSection: 'processing',
      title: t('RTV_REQUEST_SAVE_ERROR'),
      body: t('RTV_REQUEST_SAVE_ERROR_HELP'),
      video,
    });
  };

  const getPartnerLogo = () => {
    if(selectedRtvChannel.logoNames && selectedRtvChannel.logoNames.length > 0) {
      return `${Configuration.apiEndPoint}/partners/rtv_channels/${selectedRtvChannel.id}/images?load=${selectedRtvChannel.logoNames[0]}`
    }
    if (selectedPartner && selectedPartner.storeImage && selectedPartner.storeImage.length > 0) {
      return `${Configuration.apiEndPoint}/images/${selectedPartner.uid}?load=${selectedPartner.storeImage[0]}`;
    }

    return noLogo;
  };

  const styleBackgroundImageVideoWrapper = (partnerId, fieldId, fieldImage) => {
    if (partnerId && fieldImage) {
      return {backgroundImage: `url(${Configuration.apiEndPoint}/partners/fields/${fieldId}/images?load=${fieldImage})`, backgroundSize: 'cover'};
    }

    return null;
  };

  const formatVideoDate = video => `${new Date(video.timestamp).toLocaleDateString()} ${new Date(video.timestamp).toTimeString().slice(0, 5)}`;
  const formatVideoDuration = video => new Date(video.duration * 1000).toISOString().slice(11, 19);
  const academyBadge = video => video.rtvOrAcademy === rtvType.academy ? <Badge variant="info">Academy</Badge> : null;
  const videoAnalysisContentType = video => {
    if (video.rtvOrAcademy === rtvType.academy) {
      return 'academy_slot';
    }
    if (video.event) {
      return 'special_event_slot';
    }
    return 'rtv_slot';
  };

  const renderVideoNotice = () => {
    if (!videoNotice) {
      return null;
    }

    const isSaved = videoNotice.contentSection === 'saved';
    const isReady = videoNotice.contentSection === 'ready';
    const isScheduled = videoNotice.contentSection === 'scheduled';

    return (
      <div className={`public-partner-processing-notice public-partner-processing-notice-${videoNotice.contentSection || 'processing'}`}>
        <div className="public-partner-processing-icon">{isSaved || isReady ? '✓' : 'i'}</div>
        <div className="public-partner-processing-content">
          <div className="public-partner-processing-kicker">
            {isSaved || isReady ? t('RTV_VIDEO_NOTIFICATION') : isScheduled ? t('RTV_NEXT_MATCH') : t('RTV_VIDEO_STATUS')}
          </div>
          <h4>{videoNotice.title}</h4>
          <p>{videoNotice.body}</p>
          {isScheduled ?
            <div className="public-partner-processing-steps">
              <span>{t('RTV_MATCH_SCHEDULED')}</span>
              <span>{t('RTV_VIDEO_APPEARS_WHEN_READY')}</span>
            </div> :
            <div className="public-partner-processing-steps">
              <span>{t('RTV_SLICE_UPLOAD')}</span>
              <span>{t('RTV_VIDEO_MERGE')}</span>
              <span>{t('COPY_TO_MY_VIDEO')}</span>
            </div>
          }
          {videoNotice.video && !videoNotice.video.videoComplete && !isSaved && !isReady && !isScheduled ?
            <Button
              type="button"
              variant="light"
              className="public-partner-processing-action"
              disabled={videoReadyNotificationLoadingId === videoNotice.video.id}
              onClick={() => subscribeVideoReadyNotification(videoNotice.video)}
            >
              {videoReadyNotificationLoadingId === videoNotice.video.id ?
                <Fragment><Spinner animation="border" size="sm" className="mr-2"/>{t('SAVING')}</Fragment> :
                t('RTV_NOTIFY_WHEN_READY')
              }
            </Button> :
            null
          }
        </div>
      </div>
    );
  };

  const renderVideoCard = (video, section) => {
    const isSoon = section === 'soon';
    const isDelayed = video.playbackStatus === 'live_delayed';
    const fieldSport = getVideoFieldSport(video);
    const pickleballVideo = isPickleballVideo(video);

    return (
      <div
        key={video.id}
        className="col-md-12 col-lg-4 mb-sm-4 card-group"
        data-field-sport={fieldSport}
        data-pbvision-eligible={pickleballVideo}
      >
        <div className={`card h-100 public-partner-match-card ${!isSoon && !isDelayed && !video.videoComplete ? 'public-partner-match-card-live' : ''}`}>
          <div className="public-partner-match-card-accent"/>
          <div
            className="video_card_image"
            style={{...styleBackgroundImageVideoWrapper(selectedPartner.uid, video.fieldId, video.fieldImage), cursor: isSoon ? 'default' : 'pointer', display: 'block'}}
            onMouseEnter={isSoon ? undefined : preconnectVideoPlayer}
            onTouchStart={isSoon ? undefined : preconnectVideoPlayer}
            onClick={isSoon || isDelayed ? undefined : playRtvVideo(video)}
          >
            <div className="videoWrapper">
              <Fragment>
                <div className="preload h-100 justify-content-start align-items-start">
                  <img className="logo-replayer" src={video.event ? specialEvent : replayerlogoBianco} alt="replayer"/>
                </div>
                <div className="preload h-100 justify-content-end align-items-start">
                  <img className="logo-partner" src={getPartnerLogo()} alt={selectedRtvChannel.name}/>
                </div>
                <div className="preload h-100 justify-content-center align-items-end">
                  <div className="video-description justify-content-center align-items-center">{video.description ? video.description.substring(0,60) : null}</div>
                </div>
              </Fragment>
            </div>
          </div>
          <div className="card-body d-flex flex-column card_border_bottom_white">
            <div className="public-partner-match-badges">
              <Badge variant={isSoon ? 'warning' : video.videoComplete ? 'success' : 'info'}>
                {isDelayed ? t('PUBLIC_CATALOG_LIVE_DELAYED') : isSoon ? t('COMING_SOON') : video.videoComplete ? t('AVAILABLE') : t('UPLOAD_IN_PROGRESS')}
              </Badge>
              <Badge variant="dark">ReplayerTV</Badge>
              {academyBadge(video)}
            </div>
            <h5 className="card-title mt-3">{video.description}</h5>
            <h6 className="card-title regular_title_rp mb-1">{formatVideoDate(video)}</h6>
            <div className="public-partner-match-options mb-3">
              <div>
                <small>{t('FIELD')}</small>
                <strong>{video.fieldName || '-'}</strong>
              </div>
              <div>
                <small>{t('DURATION')}</small>
                <strong>{formatVideoDuration(video)}</strong>
              </div>
              {video.filesize ?
                <div>
                  <small>{t('FILE_SIZE')}</small>
                  <strong>{(Math.floor(video.filesize)/(1000000000)).toFixed(2)}GB</strong>
                </div> :
                null
              }
            </div>
            <div className="public-partner-card-actions mt-auto">
              {isDelayed ? <Button disabled className="public-partner-match-action">{t('PUBLIC_CATALOG_LIVE_DELAYED')}</Button> : !isSoon ?
                <Button type="button" variant="primary" className="public-partner-match-action" onClick={playRtvVideo(video)}>
                  {t('WATCH_MATCH')}
                </Button> :
                <Button type="button" variant="info" className="public-partner-match-action" onClick={showVideoScheduledNotice(video)}>
                  {t('VIDEO_SCHEDULED')}
                </Button>
              }
              {!isSoon && !isDelayed ?
                <Button type="button" variant="outline-primary" className="public-partner-match-action" onClick={createSliceHighlight(video)}>
                  {t('CREATE_HIGHLIGHT')}
                </Button> :
                null
              }
              {!isSoon && !isDelayed && pickleballVideo ?
                <Button type="button" variant="outline-primary" className="public-partner-match-action" onClick={createSliceAnalysis(videoAnalysisContentType(video), video)}>
                  {t('VIDEO_ANALYSIS_TITLE')}
                </Button> :
                null
              }
              {!isSoon && !isDelayed && video.videoComplete ?
                <Button type="button" variant="success" className="public-partner-match-action" onClick={addVideoToPlayerArea(video.id)}>
                  {t('COPY_TO_MY_VIDEO')}
                </Button> :
                null
              }
              {!isSoon && !isDelayed && !video.videoComplete ?
                <Button type="button" variant="info" className="public-partner-match-action" onClick={showVideoInProgressNotice(video)}>
                  {t('UPLOAD_IN_PROGRESS')}
                </Button> :
                null
              }
              {video.ownerId === profile.uid ?
                <Button type="button" variant="link" className="p-0 ml-1" onClick={confirmDeleteRtvVideo(video.id)}>
                  <img src={binIcon} alt={t('ICON_DELETE')}/>
                </Button> :
                null
              }
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="bg_lightgrey_rp" style={{transition: 'opacity 0.30s linear'}}>
        <Container fluid className="presentation-container">
          <div className="page-header header-filter clear-filter replayertv_sportcenter_hero_rp" style={styleBackgroundImage()} data-parallax="true">
            <div className={selectedPartner.isSpecialist ? "replayertv_sportcenter_hero_overlay_rp_specialist" : "replayertv_sportcenter_hero_overlay_rp"}/>
          </div>
          <div className="main main_raised_rp">
            <div className="section">
              <div className="container">
                <div className="row mb-4">
                  <div className="col-12 d-flex align-items-center flex-column">
                    <div className="replayertv_sportcenter_logo mb-2">
                      {
                        selectedRtvChannel.logoNames && selectedRtvChannel.logoNames.length > 0 ?
                        <div className="align-self-center">
                          <img
                            className="img-fluid"
                            src={`${Configuration.apiEndPoint}/partners/rtv_channels/${selectedRtvChannel.id}/images?load=${selectedRtvChannel.logoNames[0]}`}
                            alt={t('LOGO')}
                          />
                        </div> :
                          selectedPartner.storeImage && selectedPartner.storeImage.length > 0 ?
                            <div className="align-self-center">
                              <img
                                className="img-fluid"
                                src={`${Configuration.apiEndPoint}/images/${selectedPartner.uid}?load=${selectedPartner.storeImage[0]}`}
                                alt={t('LOGO')}
                              />
                            </div> :
                            <div className="align-self-center replayertv_nologo_overlay_rp"/>
                      }
                    </div>
                    <h4 className="section_title_neg_rp text-center">{selectedRtvChannel.name} <span className="lightblue_text">Replayer.tv</span></h4>
                  </div>
                  <div className="col-12">
                    {renderVideoNotice()}
                    <Tabs defaultActiveKey="watch_now" className="myvideo-tabs" style={{marginTop: 20}}>
                      <Tab eventKey="watch_now" title={t('WATCH_NOW')} tabClassName="myvideo-tab">
                        <div className="col-12" style={{background: '#00152f50', paddingTop: 15}}>
                          <div style={{display: 'flex', flexDirection: 'row-reverse', marginBottom: '10px'}}>
                            <Button
                              type="button"
                              variant="primary"
                              onClick={() => 
                                updateVideos({partnerUid: selectedPartner.uid, id: selectedRtvChannel.id})}
                              >
                              {t('UPDATE_VIDEOS')}
                            </Button>
                          </div>
                          <p className="replayertv_sportcenter_introtext">
                          {t('SEE_ALL_EVENTS_TO_SHARE')}
                          </p>
                          <p className="replayertv_sportcenter_introtext">
                          {t('SEE_ALL_EVENTS_TO_SHARE_NOW_ONLINE')}
                          </p>
                        </div>
                        <div className="row">
                          {
                            rtvVideos && rtvVideos.length > 0 ?
                              rtvVideos.filter(v => v.playbackStatus !== 'live_delayed').map(v => renderVideoCard(v, 'available'))
                            : <Fragment>
                                <div className="col-12 text-center" style={{background: '#00152f50', marginBottom: 400, paddingTop: 15}}>
                                  <p><h5>{t('NO_VIDEO')}</h5></p>
                                </div>
                              </Fragment>
                          }
                        </div>
                      </Tab>
                      {rtvVideos && rtvVideos.some(v => v.playbackStatus === 'live_delayed') && <Tab eventKey="delayed" title={t('PUBLIC_CATALOG_LIVE_DELAYED')} tabClassName="myvideo-tab">
                        <p>{t('PUBLIC_CATALOG_LIVE_DELAYED_HELP')}</p>
                        <div className="row">{rtvVideos.filter(v => v.playbackStatus === 'live_delayed').map(v => renderVideoCard(v, 'delayed'))}</div>
                      </Tab>}
                      <Tab eventKey="soon" title={t('SOON')} tabClassName="myvideo-tab"> 
                        <div className="col-12" style={{background: '#00152f50', paddingTop: 15}}>
                          <div style={{display: 'flex', flexDirection: 'row-reverse', marginBottom: '10px'}}>
                            <Button
                              type="button"
                              variant="primary"
                              onClick={() => 
                                updateVideos({partnerUid: selectedPartner.uid, id: selectedRtvChannel.id})}
                              >
                              {t('UPDATE_VIDEOS')}
                            </Button>
                          </div>
                          <p className="replayertv_sportcenter_introtext">
                          {t('SEE_ALL_EVENTS_TO_SHARE_COMING_SOON')}
                          </p>
                        </div>
                        <div className="row">
                          {
                            rtvVideosSoon && rtvVideosSoon.length > 0 ?
                              rtvVideosSoon.map(v => renderVideoCard(v, 'soon'))
                            : <Fragment>
                                <div className="col-lg-12 mt-sm-2 text-center" style={{background: '#00152f50', marginBottom: 400, paddingTop: 15}}>
                                  <p><h5>{t('NO_VIDEO_SOON')}</h5></p>
                                </div>
                              </Fragment>
                          }
                        </div>
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Fade>
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
      {
        selectedPermanentVideo === null ?
          null :
          <PlayerHighLight video={selectedPermanentVideo}/>
      }
    </Fragment>
  );
};

PlayerReplayerTv.propTypes = {
  token: PropTypes.string,
  selectedPartner: PropTypes.object,
  rtvVideos: PropTypes.array,
  rtvVideosSoon: PropTypes.array,
  addVideoToPlayerArea: PropTypes.func.isRequired,
  deleteRtvVideo: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  rtvVideosPlaying: PropTypes.array,
  addRtvVideoPlaying: PropTypes.func.isRequired,
  updateVideos: PropTypes.func.isRequired,
  selectedRtvChannel: PropTypes.object,
  selectedSpecialEvent: PropTypes.object,
  selectedPermanentVideo: PropTypes.object,
  createSliceAnalysis: PropTypes.func.isRequired,
}

PlayerReplayerTv.defaultProps = {
  token: null,
  selectedPartner: null,
  rtvVideos: [],
  rtvVideosSoon: [],
  rtvVideosPlaying: [],
  selectedRtvChannel: null,
  selectedSpecialEvent: null,
  selectedPermanentVideo: null
};

const mapStateToProps = ({login: {token, profile}, player: {selectedPartner, rtvVideos, rtvVideosSoon, rtvVideosPlaying, selectedRtvChannel, selectedSpecialEvent, selectedPermanentVideo}}) => ({
  token,
  selectedPartner,
  rtvVideos,
  rtvVideosSoon,
  profile,
  rtvVideosPlaying,
  selectedRtvChannel,
  selectedSpecialEvent,
  selectedPermanentVideo
});

const mapDispatchToProps = dispatch => ({
  addVideoToPlayerArea: slotId => () => {
    dispatch(addVideoToPlayerArea(slotId));
    dispatch(push('/player-video'));
  },
  deleteRtvVideo: slotId => () => {
    dispatch(deleteRtvVideo(slotId));
  },
  addRtvVideoPlaying: videoId => () => {
    dispatch(addRtvVideoPlaying(videoId));
  },
  updateVideos: (channel, eventId = null) => {
    dispatch(getRtvVideos(channel.partnerUid, channel.id, eventId));
  },
  sendUsageData: (partnerId, rtvChannelId, slotId, streaming) => {
    dispatch(postUsageData(1, partnerId, rtvChannelId, slotId, streaming, 1));
  },
  createSliceHighlight: video => event => {
    event.preventDefault();
    dispatch(permanentVideoSelected({
      videoId: null,
      slotId: video.id,
      sliceHighlight: true,
      name: video.description,
      token: video.token,
      cameras: video.cameras || [],
    }));
    dispatch(showVideoHighlightModal());
  },
  createSliceAnalysis: (contentType, video) => event => {
    event.preventDefault();
    dispatch(permanentVideoSelected({
      videoId: null,
      slotId: video.id,
      contentType,
      contentId: video.id,
      analysisMode: true,
      sliceHighlight: true,
      name: video.description,
      analysisTitle: `Analisi ${video.description}`,
      token: video.token,
      cameras: video.cameras || [],
    }));
    dispatch(showVideoHighlightModal());
  },
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerReplayerTv));
